import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import '../styles/design-system.css';
import './EventsPage.css';

const PAGE_SIZE = 50;

const IP = { viewBox:'0 0 24 24', fill:'none', stroke:'currentColor', strokeWidth:2, strokeLinecap:'round', strokeLinejoin:'round' };
const Icon = {
  Refresh:  (p) => (<svg width="14" height="14" {...IP} {...p}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>),
  Empty:    (p) => (<svg width="40" height="40" {...IP} strokeWidth="1.5" {...p}><path d="M13 2 3 14h9l-1 8 10-12h-9z"/></svg>),
  Filter:   (p) => (<svg width="14" height="14" {...IP} {...p}><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>),
  ChevDown: (p) => (<svg width="12" height="12" {...IP} {...p}><path d="m6 9 6 6 6-6"/></svg>),
  Calendar: (p) => (<svg width="14" height="14" {...IP} {...p}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>),
  Analytics: (p) => (<svg width="14" height="14" {...IP} {...p}><path d="M3 3v18h18"/><path d="m7 14 4-4 4 4 5-5"/></svg>),
  Export:   (p) => (<svg width="14" height="14" {...IP} {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>),
  Dots:     (p) => (<svg width="14" height="14" {...IP} {...p}><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>),
  Desktop:  (p) => (<svg width="14" height="14" {...IP} {...p}><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>),
  Mobile:   (p) => (<svg width="14" height="14" {...IP} {...p}><rect width="14" height="20" x="5" y="2" rx="2"/><path d="M12 18h.01"/></svg>),
  Tablet:   (p) => (<svg width="14" height="14" {...IP} {...p}><rect width="16" height="20" x="4" y="2" rx="2"/><path d="M12 18h.01"/></svg>),
};

const SPARK_PATHS = {
  clicks: 'M0,24 L12,22 L24,18 L36,20 L48,15 L60,12 L72,8 L84,10 L100,4',
  leads:  'M0,22 L12,20 L24,18 L36,15 L48,16 L60,11 L72,12 L84,7 L100,8',
  sales:  'M0,25 L12,22 L24,24 L36,18 L48,20 L60,14 L72,15 L84,9 L100,6',
};

const SPARK_COLORS = { clicks:'#ff5436', leads:'#8b9b3f', sales:'#e8b94a' };

function StatSparkline({ type }) {
  const color = SPARK_COLORS[type];
  const path  = SPARK_PATHS[type];
  const gradId = `sparkGrad-${type}`;
  const areaPath = `${path} L100,30 L0,30 Z`;
  return (
    <svg className="ev-stat-spark" viewBox="0 0 100 30" preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.45"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`}/>
      <path d={path} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const CC = {'United States':'us','USA':'us','US':'us','Netherlands':'nl','NL':'nl','Germany':'de','DE':'de','United Kingdom':'gb','UK':'gb','GB':'gb','France':'fr','FR':'fr','Japan':'jp','JP':'jp','Canada':'ca','CA':'ca','India':'in','IN':'in','Australia':'au','AU':'au','Brazil':'br','BR':'br'};
function cc(name) {
  if (!name) return 'un';
  if (CC[name]) return CC[name];
  const t = String(name).trim();
  if (t.length === 2) return t.toLowerCase();
  if (CC[t]) return CC[t];
  return 'un';
}
function Flag({ code, width=18, height=13 }) {
  const c = code || 'un';
  return (
    <span className={`flag ${c}`} style={{ width, height, display:'inline-flex', flexShrink:0, border:'1px solid rgba(13,13,15,0.08)', borderRadius:2 }}>
      {c === 'nl' && <><span/><span/><span/></>}
    </span>
  );
}

function deviceIcon(device) {
  const d = (device || '').toLowerCase();
  if (d.includes('mobile') || d === 'phone') return <Icon.Mobile />;
  if (d.includes('tablet')) return <Icon.Tablet />;
  return <Icon.Desktop />;
}

function relativeTime(iso) {
  if (!iso) return '—';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return new Date(iso).toLocaleDateString('en-US', { month:'short', day:'numeric' });
}

function absoluteTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const h = d.getHours(), m = d.getMinutes();
  return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`;
}

function formatCurrency(v, currency='USD') {
  if (v == null) return '';
  try { return new Intl.NumberFormat('en-US', { style:'currency', currency }).format(v); }
  catch { return `$${v}`; }
}

const FAV_VARIANTS = ['','olive','dark','mustard'];

function groupByDay(events) {
  const now = new Date();
  const today = new Date(now); today.setHours(0,0,0,0);
  const yesterday = new Date(today); yesterday.setDate(today.getDate()-1);
  const groups = new Map();
  for (const e of events) {
    const d = e.date ? new Date(e.date) : null;
    let label = 'Earlier';
    if (d) {
      const ed = new Date(d); ed.setHours(0,0,0,0);
      if (ed.getTime() === today.getTime()) label = 'Today';
      else if (ed.getTime() === yesterday.getTime()) label = 'Yesterday';
      else label = d.toLocaleDateString('en-US', { month:'short', day:'numeric' });
    }
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(e);
  }
  return Array.from(groups.entries()).map(([label, rows]) => ({ label, rows }));
}

export default function EventsPage() {
  const { workspace } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents]         = useState([]);
  const [stats, setStats]           = useState({ clicks:0, leads:0, sales:0 });
  const [loading, setLoading]       = useState(true);
  const [live, setLive]             = useState(true);
  const [activeStat, setActiveStat] = useState('clicks');
  const [typeFilter, setTypeFilter] = useState('all');
  const channelRef  = useRef(null);
  const slugCache   = useRef({});

  const loadEvents = useCallback(async () => {
    if (!workspace?.workspace_id) return;
    try {
      setLoading(true);
      const wsId = workspace.workspace_id;
      const since = new Date(Date.now() - 30*24*60*60*1000).toISOString();

      const { count: clickCount } = await supabase
        .from('clicks').select('*', { count:'exact', head:true })
        .eq('workspace_id', wsId).gte('timestamp', since).eq('is_bot', false);

      const { data: convRows } = await supabase
        .from('conversions').select('event_name, value')
        .eq('workspace_id', wsId).gte('created_at', since);

      const leadCount = (convRows||[]).filter(r => r.event_name === 'lead').length;
      const saleSum   = (convRows||[]).filter(r => r.event_name === 'sale').reduce((a,r) => a+(r.value||0), 0);
      setStats({ clicks: clickCount||0, leads: leadCount, sales: saleSum });

      const { data: recentClicks } = await supabase
        .from('clicks').select('click_id,link_id,timestamp,country,device_type,referrer,is_bot,utm_source')
        .eq('workspace_id', wsId).eq('is_bot', false)
        .order('timestamp', { ascending: false }).limit(PAGE_SIZE);

      const linkIds = [...new Set((recentClicks||[]).map(c => c.link_id).filter(Boolean))];
      if (linkIds.length > 0) {
        const { data: slugRows } = await supabase.from('links').select('link_id,slug').in('link_id', linkIds);
        (slugRows||[]).forEach(r => { slugCache.current[r.link_id] = r.slug; });
      }

      const clickEvents = (recentClicks||[]).map(c => ({
        id: c.click_id, type:'click', date: c.timestamp, link_id: c.link_id,
        slug: slugCache.current[c.link_id]||null, country: c.country,
        device: c.device_type, referrer: c.referrer, amount: null,
      }));

      const { data: recentConv } = await supabase
        .from('conversions').select('*').eq('workspace_id', wsId)
        .order('created_at', { ascending: false }).limit(PAGE_SIZE);

      const convEvents = (recentConv||[]).map(c => ({
        id: c.conversion_id, type: c.event_name||'conversion', date: c.created_at,
        link_id: c.link_id, slug: slugCache.current[c.link_id]||null,
        country: null, device: null, referrer: null, amount: c.value, currency: c.currency,
      }));

      setEvents([...clickEvents, ...convEvents].sort((a,b) => new Date(b.date)-new Date(a.date)).slice(0, PAGE_SIZE));
    } catch (err) {
      console.error('Events load error', err);
    } finally {
      setLoading(false);
    }
  }, [workspace?.workspace_id]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  useEffect(() => {
    if (!workspace?.workspace_id || !live) {
      channelRef.current?.unsubscribe();
      channelRef.current = null;
      return;
    }
    const wsId = workspace.workspace_id;
    const ch = supabase.channel(`events:${wsId}`)
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'clicks', filter:`workspace_id=eq.${wsId}` },
        (payload) => {
          const c = payload.new;
          if (c.is_bot) return;
          setStats(s => ({ ...s, clicks: s.clicks+1 }));
          setEvents(prev => [{
            id: c.click_id, type:'click', date: c.timestamp, link_id: c.link_id,
            slug: slugCache.current[c.link_id]||null, country: c.country,
            device: c.device_type, referrer: c.referrer, amount: null, fresh: true,
          }, ...prev].slice(0, PAGE_SIZE));
        }
      )
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'conversions', filter:`workspace_id=eq.${wsId}` },
        (payload) => {
          const c = payload.new;
          setStats(s => ({
            ...s,
            leads: c.event_name==='lead' ? s.leads+1 : s.leads,
            sales: c.event_name==='sale' ? s.sales+(c.value||0) : s.sales,
          }));
          setEvents(prev => [{
            id: c.conversion_id, type: c.event_name||'conversion', date: c.created_at,
            link_id: c.link_id, slug: slugCache.current[c.link_id]||null,
            amount: c.value, currency: c.currency, fresh: true,
          }, ...prev].slice(0, PAGE_SIZE));
        }
      ).subscribe();
    channelRef.current = ch;
    return () => { ch.unsubscribe(); };
  }, [workspace?.workspace_id, live]);

  const visibleEvents = typeFilter==='all' ? events
    : typeFilter==='clicks' ? events.filter(e => e.type==='click')
    : typeFilter==='leads'  ? events.filter(e => e.type==='lead')
    : typeFilter==='sales'  ? events.filter(e => e.type==='sale')
    : events;

  const grouped = groupByDay(visibleEvents);

  /* stat card counter animation */
  const [animStats, setAnimStats] = useState({ clicks:'0', leads:'0', sales:'$0' });
  useEffect(() => {
    const duration = 1500;
    const start = performance.now();
    const { clicks, leads, sales } = stats;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1-p, 3);
      setAnimStats({
        clicks: Math.floor(clicks * eased).toLocaleString(),
        leads:  Math.floor(leads  * eased).toLocaleString(),
        sales:  '$' + Math.floor(sales * eased).toLocaleString(),
      });
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [stats.clicks, stats.leads, stats.sales]);

  return (
    <div className="ds-page" data-testid="events-page">
      {/* PAGE HEADER */}
      <div className="page-header">
        <div>
          <div className="page-label">Live · streaming now</div>
          <h1 className="page-title">Events <em>stream</em>.</h1>
          <p className="page-sub">Every click, conversion, and scan from your workspace — in realtime.</p>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="ev-toolbar">
        <div className="ev-toolbar-left">
          <button className="ev-tool-btn">
            <Icon.Filter />
            Filter
            <Icon.ChevDown />
          </button>
          <button className="ev-tool-btn">
            <Icon.Calendar />
            Last 24 hours
            <Icon.ChevDown />
          </button>
        </div>
        <div className="ev-toolbar-right">
          <button className="ev-tool-btn" onClick={() => navigate('/analytics')}>
            <Icon.Analytics />
            View analytics
          </button>
          <button className="ev-tool-btn">
            <Icon.Export />
            Export CSV
          </button>
          <button
            className={`ev-tool-btn${live?' ev-tool-live':''}`}
            onClick={() => setLive(l => !l)}
            title={live ? 'Pause live stream' : 'Resume live stream'}
          >
            <span className={`ev-live-dot${live?'':' paused'}`}/>
            {live ? 'Live' : 'Paused'}
          </button>
          <button className="ev-tool-btn icon-only" onClick={loadEvents}>
            <Icon.Refresh />
          </button>
        </div>
      </div>

      {/* STAT CARDS */}
      <div className="ev-stats">
        {(['clicks','leads','sales']).map(key => (
          <button
            key={key}
            className={`ev-stat${activeStat===key?' active':''}`}
            onClick={() => { setActiveStat(key); setTypeFilter(key==='clicks'?'clicks':key==='leads'?'leads':'sales'); }}
          >
            <div className="ev-stat-info">
              <div className="ev-stat-label">{key.charAt(0).toUpperCase()+key.slice(1)}</div>
              <div className="ev-stat-value">{animStats[key]}</div>
            </div>
            <StatSparkline type={key}/>
          </button>
        ))}
      </div>

      {/* EVENTS TABLE */}
      <div className="ev-table-wrap">
        <div className="ev-table-head">
          <div className="ev-th">
            Date
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{opacity:0.4}}>
              <path d="m7 15 5 5 5-5"/><path d="m7 9 5-5 5 5"/>
            </svg>
          </div>
          <div className="ev-th">Link</div>
          <div className="ev-th">Referrer</div>
          <div className="ev-th">Country</div>
          <div className="ev-th">Device</div>
          <div className="ev-th"/>
        </div>

        <div className="ev-rows" id="ev-rows">
          {loading ? (
            <div className="ds-empty">
              <div className="ds-skeleton" style={{height:14,width:200,marginBottom:8}}/>
              <div className="ds-skeleton" style={{height:14,width:280}}/>
            </div>
          ) : visibleEvents.length === 0 ? (
            <div className="ds-empty">
              <div className="ds-empty-icon"><Icon.Empty /></div>
              <div className="ds-empty-title">No events yet</div>
              <div className="ds-empty-sub">Events appear here as your links receive clicks and conversions.</div>
            </div>
          ) : (
            grouped.map(({ label, rows }) => (
              <div key={label}>
                <div className="ev-date-divider">{label}</div>
                {rows.map((ev, i) => {
                  const flagCode = cc(ev.country);
                  const refDirect = !ev.referrer || ev.referrer === 'direct';
                  const refInitial = ev.referrer ? ev.referrer.replace(/https?:\/\//,'').charAt(0).toUpperCase() : '→';
                  return (
                    <div
                      key={ev.id || `${label}-${i}`}
                      className={`ev-row${ev.fresh?' streaming-in is-new':''}`}
                      onClick={() => ev.link_id && navigate(`/links/${ev.link_id}`)}
                      style={ev.link_id ? { cursor:'pointer' } : undefined}
                    >
                      <div className="ev-date">
                        {absoluteTime(ev.date)}
                        <span className="ev-time-rel">{relativeTime(ev.date)}</span>
                      </div>

                      <div className="ev-link-cell">
                        <div className={`ev-link-icon ${FAV_VARIANTS[i%4]}`}>
                          {(ev.slug || ev.link_id || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="ev-link-info">
                          <div className="ev-link-slug">
                            {ev.slug ? `lfo.to/${ev.slug}` : ev.link_id || '—'}
                            {ev.type === 'lead' && <span className="ev-type-tag lead" style={{marginLeft:6}}>Lead</span>}
                            {ev.type === 'sale' && <span className="ev-type-tag sale" style={{marginLeft:6}}>Sale</span>}
                          </div>
                          <div className="ev-link-type">
                            {ev.type === 'sale' && ev.amount != null
                              ? <span className="ev-sale-amount">+ {formatCurrency(ev.amount, ev.currency||'USD')}</span>
                              : ev.type.toUpperCase()}
                          </div>
                        </div>
                      </div>

                      <div className={`ev-referrer${refDirect?' direct':''}`}>
                        <span className="ev-referrer-icon" style={!refDirect ? { background:'#4a4a52', color:'white' } : undefined}>
                          {refDirect ? '→' : refInitial}
                        </span>
                        {refDirect ? '(direct)' : ev.referrer?.replace(/https?:\/\//,'') || '—'}
                      </div>

                      <div className="ev-country">
                        {ev.country ? (
                          <><Flag code={flagCode}/> {ev.country}</>
                        ) : <span style={{color:'var(--ink-faint)'}}>—</span>}
                      </div>

                      <div className="ev-device">
                        {ev.device ? <><span className="ev-device-icon">{deviceIcon(ev.device)}</span><span>{ev.device.split(' ')[0]}</span></> : <span style={{color:'var(--ink-faint)'}}>—</span>}
                      </div>

                      <div className="ev-more">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
                        </svg>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
